import type { ScenarioSpec } from "../scenario/types";
import { getHostConfig } from "./hostConfig";
import { setChromeOverlay } from "./tx";

/**
 * Locale overlay + active-locale framework (Change 8 / Phase 6). Localization
 * is a TRANSLATION problem, not a new voice pipeline: the server TTS
 * (/api/tts -> OpenAI gpt-4o-mini-tts, or /sim/tts when Cyberfrog-hosted)
 * speaks whatever language the TEXT is written in and needs no server-side
 * language switch. The `lang` (BCP-47) tag passed around the codebase only
 * feeds the browser `speechSynthesis` FALLBACK voice picker (see
 * `lib/audio.ts`'s `speakViaBrowser`) for the rare case the server TTS
 * request fails outright, so keeping it in sync with the active locale only
 * matters for that fallback path.
 *
 * A locale overlay is a flat `{ "<path>": "<translated text>" }` JSON file
 * (see `extract-i18n.mjs`) keyed by dot-separated object keys + array steps,
 * e.g. "beats[id=usb-decision].guide.title" or
 * "devices[id=laptop].config.emails[3].qr.options.enter.label".
 * `applyLocaleOverlay` walks those paths against a CLONE of the base English
 * ScenarioSpec so beat ids / edges / scoring (the structural source of
 * truth) never change per locale -- only the leaf strings each path points at.
 *
 * Array steps come in two flavors: `[id=<value>]` resolves by scanning the
 * array for an element whose `.id` equals `<value>` (used for `beats[]` and
 * `devices[]`, both of which carry a stable string id and are the arrays
 * most likely to be reordered/inserted/deleted during content edits — this
 * is what makes translations survive a beat being added, removed, or moved
 * without going stale or landing on the wrong beat). Plain `[n]` numeric
 * steps are positional and used for arrays whose elements have no natural
 * id (e.g. a beat's inline `guide.copies[]`); those are nested *inside* an
 * already id-anchored beat/device, so reordering them independently of
 * their parent is rare, and if it does happen `applyLocaleOverlay` degrades
 * the same way it always has: an unresolvable path is silently skipped and
 * that one string falls back to English rather than corrupting anything.
 */

export interface LocaleOption {
  code: string;
  label: string;
  ttsLang: string;
}

export const SUPPORTED_LOCALES: LocaleOption[] = [
  { code: "en", label: "English", ttsLang: "en-US" },
  { code: "de", label: "Deutsch", ttsLang: "de-DE" },
  { code: "fr", label: "Français", ttsLang: "fr-FR" },
  { code: "es", label: "Español", ttsLang: "es-ES" },
];

const STORAGE_KEY = "cf_sim_lang";

// Bundled overlays are only a fallback now (office-under-attack ships its own
// files); the published translations come from the backend at runtime.
const overlayModules = import.meta.glob("../locales/*.json") as Record<
  string,
  () => Promise<{ default: Record<string, string> }>
>;

function overlayKey(scenarioId: string, locale: string): string {
  return `../locales/${scenarioId}.${locale}.json`;
}

/** Standalone 3D site: the learner plane origin. Hosted (/t/ pages): same origin. */
const I18N_ORIGIN = "https://awareness.cyberfrogsecurity.com";

function i18nBase(): string {
  const host = getHostConfig();
  return host.hosted ? host.apiBase : I18N_ORIGIN;
}

function urlParam(name: string): string | null {
  try {
    return new URLSearchParams(window.location.search).get(name);
  } catch {
    return null;
  }
}

/** `?i18nPreview=<token>`: a staged translation that is not published yet. */
export function i18nPreviewToken(): string | null {
  const v = urlParam("i18nPreview");
  return v && /^[A-Za-z0-9._~-]{1,200}$/.test(v) ? v : null;
}

function previewLocale(): string | null {
  if (!i18nPreviewToken()) return null;
  const l = urlParam("lang");
  return l && SUPPORTED_LOCALES.some((s) => s.code === l) ? l : null;
}

async function getJson(url: string, timeoutMs: number): Promise<unknown | null> {
  const ctl = typeof AbortController !== "undefined" ? new AbortController() : null;
  const timer = ctl ? window.setTimeout(() => ctl.abort(), timeoutMs) : 0;
  try {
    const res = await fetch(url, { credentials: "omit", cache: "no-cache", signal: ctl?.signal });
    if (!res.ok) return null;
    return await res.json();
  } catch {
    return null;
  } finally {
    if (timer) window.clearTimeout(timer);
  }
}

/** One backend overlay (`_chrome` = the shared player chrome). A preview token
 *  that does not belong to this id falls back to the published overlay. */
export async function fetchRemoteOverlay(
  scenarioId: string,
  locale: string,
  timeoutMs = 4000,
): Promise<Record<string, string> | null> {
  if (!locale || locale === "en" || !scenarioId) return null;
  const base = `${i18nBase()}/sim/locales/${encodeURIComponent(scenarioId)}/${encodeURIComponent(locale)}.json`;
  const token = i18nPreviewToken();
  let data = (await getJson(token ? `${base}?preview=${encodeURIComponent(token)}` : base, timeoutMs)) as
    | { overlay?: Record<string, unknown> }
    | null;
  if (!data && token) data = (await getJson(base, timeoutMs)) as { overlay?: Record<string, unknown> } | null;
  const overlay = data && typeof data === "object" ? data.overlay : null;
  if (!overlay || typeof overlay !== "object") return null;
  const out: Record<string, string> = {};
  for (const [k, v] of Object.entries(overlay)) if (typeof v === "string") out[k] = v;
  return Object.keys(out).length ? out : null;
}

const remoteLocales = new Map<string, Set<string>>();

/** Published languages of a course from the backend index (call once at boot). */
export async function loadLocaleIndex(scenarioId: string, timeoutMs = 3000): Promise<void> {
  if (!scenarioId || remoteLocales.has(scenarioId)) return;
  const data = (await getJson(`${i18nBase()}/sim/locales/${encodeURIComponent(scenarioId)}/index.json`, timeoutMs)) as
    | { locales?: unknown }
    | null;
  const list = data && Array.isArray(data.locales) ? data.locales.filter((x): x is string => typeof x === "string") : [];
  remoteLocales.set(scenarioId, new Set(list));
}

export function isLocaleAvailable(scenarioId: string, locale: string): boolean {
  if (locale === "en") return true;
  if (overlayKey(scenarioId, locale) in overlayModules) return true;
  if (remoteLocales.get(scenarioId)?.has(locale)) return true;
  return previewLocale() === locale;
}

/** Course overlay: the backend one merged over a bundled file (fallback only). */
export async function loadLocaleOverlay(
  scenarioId: string,
  locale: string,
): Promise<Record<string, string> | null> {
  if (locale === "en") return null;
  const importer = overlayModules[overlayKey(scenarioId, locale)];
  const [bundled, remote] = await Promise.all([
    importer
      ? importer()
          .then((mod) => mod.default ?? (mod as unknown as Record<string, string>))
          .catch(() => null)
      : Promise.resolve(null),
    fetchRemoteOverlay(scenarioId, locale),
  ]);
  if (!bundled && !remote) return null;
  return { ...(bundled ?? {}), ...(remote ?? {}) };
}

/** The shared chrome overlay for a locale, stored for tx()/t(). */
export async function loadChromeOverlay(locale: string): Promise<void> {
  setChromeOverlay(locale === "en" ? null : await fetchRemoteOverlay("_chrome", locale));
}

/** An `[id=<value>]` path segment resolves against an array element's
 *  stable `.id` field instead of its (reorder-fragile) numeric position. */
type IdToken = { idKey: string };
type PathToken = string | number | IdToken;

/**
 * Tokenizes a path into its component accessors, e.g.
 * "beats[id=usb-decision].guide.title" -> ["beats", {idKey:"usb-decision"},
 * "guide", "title"], or the legacy "beats[12].guide.title" ->
 * ["beats", 12, "guide", "title"] (numeric-index paths are still supported
 * so any not-yet-migrated overlay entry keeps working).
 */
function pathTokens(path: string): PathToken[] {
  const tokens: PathToken[] = [];
  const re = /([^.[\]]+)|\[id=([^\]]+)\]|\[(\d+)\]/g;
  let m: RegExpExecArray | null;
  while ((m = re.exec(path))) {
    if (m[2] !== undefined) tokens.push({ idKey: m[2] });
    else if (m[3] !== undefined) tokens.push(Number(m[3]));
    else tokens.push(m[1] as string);
  }
  return tokens;
}

function isIdToken(t: PathToken): t is IdToken {
  return typeof t === "object" && t !== null && "idKey" in t;
}

/** Resolves one navigation step. Array steps prefer id-matching for
 *  `{idKey}` tokens (stable across reordering/insertion/deletion); numeric
 *  tokens keep the legacy positional lookup. */
function step(cur: unknown, t: PathToken): unknown {
  if (cur == null) return undefined;
  if (isIdToken(t)) {
    if (!Array.isArray(cur)) return undefined;
    return cur.find((el) => el && typeof el === "object" && (el as { id?: unknown }).id === t.idKey);
  }
  return (cur as Record<string, unknown>)[t as string | number];
}

/**
 * Applies a flat overlay onto a DEEP CLONE of `scenario` and returns the
 * clone -- the original (REGISTRY singleton) is never mutated, so switching
 * locales repeatedly (including back to English) always starts from the same
 * pristine source. Unknown/stale paths are skipped rather than throwing, so a
 * leftover overlay entry after a later content edit degrades to English for
 * that one string instead of breaking the course.
 */
export function applyLocaleOverlay(
  scenario: ScenarioSpec,
  overlay: Record<string, string>,
): ScenarioSpec {
  const clone = structuredClone(scenario) as unknown as Record<string, unknown>;
  for (const [path, value] of Object.entries(overlay)) {
    const tokens = pathTokens(path);
    if (!tokens.length) continue;
    let cur: unknown = clone;
    let ok = true;
    for (let i = 0; i < tokens.length - 1; i++) {
      const next = step(cur, tokens[i]);
      if (next === undefined || next === null) {
        ok = false;
        break;
      }
      cur = next;
    }
    if (!ok || cur == null || typeof cur !== "object") continue;
    const last = tokens[tokens.length - 1];
    // The leaf is usually a plain object field name, but plain string
    // arrays (e.g. a beat's `topics`/`meta` chips) end on a numeric index
    // instead -- that IS the leaf there, not an intermediate step, so it
    // still needs a direct assignment (dropping this case silently
    // stranded every `topics[n]`/`meta[n]` translation as English-only).
    if (isIdToken(last)) continue; // no generated path ends on an id-object
    if (typeof last === "number") {
      if (!Array.isArray(cur) || last < 0 || last >= cur.length) continue;
      (cur as unknown[])[last] = value;
      continue;
    }
    if (!(last in (cur as Record<string, unknown>))) continue;
    (cur as Record<string, unknown>)[last] = value;
  }
  return clone as unknown as ScenarioSpec;
}

function resolveInitialLocale(): string {
  try {
    const url = new URLSearchParams(window.location.search).get("lang");
    if (url && SUPPORTED_LOCALES.some((l) => l.code === url)) return url;
  } catch {
    /* non-browser guard, never hit in this SPA */
  }
  const host = getHostConfig();
  if (host.language) {
    const short = host.language.slice(0, 2).toLowerCase();
    if (SUPPORTED_LOCALES.some((l) => l.code === short)) return short;
  }
  try {
    const stored = window.localStorage.getItem(STORAGE_KEY);
    if (stored && SUPPORTED_LOCALES.some((l) => l.code === stored)) return stored;
  } catch {
    /* private-mode / storage disabled */
  }
  return "en";
}

let activeLocale: string = resolveInitialLocale();

export function getActiveLocale(): string {
  return activeLocale;
}

export function setActiveLocale(locale: string): void {
  activeLocale = locale;
  try {
    window.localStorage.setItem(STORAGE_KEY, locale);
  } catch {
    /* private-mode / storage disabled */
  }
}

/** BCP-47 tag for the active locale -- browser-speechSynthesis FALLBACK only
 *  (see file header); never sent to the server TTS. */
export function activeTtsLang(): string {
  return SUPPORTED_LOCALES.find((l) => l.code === activeLocale)?.ttsLang ?? "en-US";
}
